import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import { fetchRoomMedia, normalizeMediaUrl } from "../backend/mediaService";
import {
  FaArrowLeft,
  FaBed,
  FaUserFriends,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaWifi,
  FaSnowflake,
  FaStar,
} from "react-icons/fa";


import { useSelector } from "react-redux";
import { resolvePriceFromMap } from "../utils/priceHelper";

const ROOM_TYPES = {
  standardAC: {
    title: "Standard AC Room",
    defaultPrice: 2500,
    maxGuests: "2 Guests",
    bed: "1 King Bed",
    features: ["Air Conditioned", "Attached Modern Bath", "Garden View", "Free Wi-Fi"],
    fallbackImages: [
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_Ac_1.jpg?alt=media&token=5ff1f018-4def-4ab8-a438-88840d6f5060",
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_2_ac.jpg?alt=media&token=cc127106-6d2a-4ba2-961c-0c954c4adc0a",
    ],
  },
  standardNonAC: {
    title: "Standard Non-AC Room",
    defaultPrice: 2000,
    maxGuests: "2 Guests",
    bed: "1 King Bed",
    features: ["Ceiling Fans", "Attached Clean Bath", "Natural Coastal Breeze", "Free Wi-Fi"],
    fallbackImages: [
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_2_ac.jpg?alt=media&token=cc127106-6d2a-4ba2-961c-0c954c4adc0a",
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_Ac_1.jpg?alt=media&token=5ff1f018-4def-4ab8-a438-88840d6f5060",
    ],
  },
  doubleRoomAC: {
    title: "Double Room AC (Family Suite)",
    defaultPrice: 4800,
    maxGuests: "4 - 6 Guests",
    bed: "2 Double Beds",
    features: ["Air Conditioned", "Balcony Sit-out", "Spacious Family Living", "Attached Bath", "Free Wi-Fi"],
    fallbackImages: [
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomAC%2FRM5.jpg?alt=media&token=ae1846dc-c7b7-4c60-89d7-7eeb3f258cfd",
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomAC%2FRM4.jpg?alt=media&token=0bf942b5-6135-455b-8c28-b35f76597cb2",
    ],
  },
  doubleRoomNonAC: {
    title: "Double Room Non-AC (Family)",
    defaultPrice: 4000,
    maxGuests: "4 - 6 Guests",
    bed: "2 Double Beds",
    features: ["Large Room Layout", "Balcony View", "Attached Clean Bath", "Free Wi-Fi"],
    fallbackImages: [
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomNonAC%2Fc0224fc2-45a4-4d63-822d-07fa4c7839ab.jpeg?alt=media&token=91bec3ec-7cc8-4bd1-9a69-faaf61d0a5f2",
      "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomAC%2FRM4.jpg?alt=media&token=0bf942b5-6135-455b-8c28-b35f76597cb2",
    ],
  },
};

const RoomImagesPage = () => {
  const { roomType } = useParams();
  const navigate = useNavigate();
  const validRoomKeys = Object.keys(ROOM_TYPES);
  const configData = useSelector((state) => state.config?.data);
  const mediaState = useSelector((state) => state.media);

  const [selectedRoom, setSelectedRoom] = useState(
    roomType && validRoomKeys.includes(roomType) ? roomType : "standardAC"
  );
  const [media, setMedia] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(null);

  const getDynamicPrice = (roomKey) => {
    const title = ROOM_TYPES[roomKey]?.title || roomKey;
    const defaultVal = ROOM_TYPES[roomKey]?.defaultPrice || 2500;
    return resolvePriceFromMap(configData?.price_map, title, defaultVal);
  };



  useEffect(() => {
    if (roomType && validRoomKeys.includes(roomType)) {
      setSelectedRoom(roomType);
    }
  }, [roomType]);

  useEffect(() => {
    const fetchMediaData = async () => {
      setLoading(true);
      try {
        // Try room specific media first
        const res = await fetchRoomMedia(selectedRoom);

        let rawImages = [];
        let rawVideos = [];

        if (res) {
          rawImages = res.images || [];
          rawVideos = res.videos || [];
        } else if (mediaState?.rooms?.[selectedRoom]) {
          // Fallback to cached Redux media
          const roomObj = mediaState.rooms[selectedRoom];
          rawImages = roomObj.images || [];
          rawVideos = roomObj.videos || [];
        }

        // Normalize images into uniform structure
        const normalizedImages = rawImages.map((img, idx) => {
          if (typeof img === "string") {
            return {
              url: img,
              title: `${ROOM_TYPES[selectedRoom]?.title || selectedRoom} Photo ${idx + 1}`,
              isCover: idx === 0,
            };
          }
          return {
            url: normalizeMediaUrl(img),
            title: img.title || `${ROOM_TYPES[selectedRoom]?.title || selectedRoom} Photo ${idx + 1}`,
            isCover: Boolean(img.isCover),
            isFeatured: Boolean(img.isFeatured),
          };
        }).filter((item) => Boolean(item.url));

        // Normalize videos
        const normalizedVideos = rawVideos.map((vid, idx) => {
          if (typeof vid === "string") {
            return {
              url: vid,
              title: `${ROOM_TYPES[selectedRoom]?.title || selectedRoom} Video ${idx + 1}`,
            };
          }
          return {
            url: normalizeMediaUrl(vid),
            title: vid.title || `${ROOM_TYPES[selectedRoom]?.title || selectedRoom} Video ${idx + 1}`,
          };
        }).filter((item) => Boolean(item.url));

        // If backend returned empty, use rich fallbacks
        const imagesToUse =
          normalizedImages.length > 0
            ? normalizedImages
            : (ROOM_TYPES[selectedRoom]?.fallbackImages || []).map((url, idx) => ({
                url,
                title: `${ROOM_TYPES[selectedRoom]?.title || selectedRoom} Photo ${idx + 1}`,
                isCover: idx === 0,
              }));

        setMedia({
          images: imagesToUse,
          videos: normalizedVideos,
        });
      } catch (err) {
        console.error("Error fetching room media:", err);
        setMedia({
          images: (ROOM_TYPES[selectedRoom]?.fallbackImages || []).map((url, idx) => ({
            url,
            title: `${ROOM_TYPES[selectedRoom]?.title || selectedRoom} Photo ${idx + 1}`,
            isCover: idx === 0,
          })),
          videos: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMediaData();
  }, [selectedRoom, mediaState?.rooms]);

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (currentIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : media.images.length - 1));
      } else if (e.key === "ArrowRight") {
        setCurrentIndex((prev) => (prev < media.images.length - 1 ? prev + 1 : 0));
      } else if (e.key === "Escape") {
        setCurrentIndex(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, media.images.length]);

  const currentRoomMeta = ROOM_TYPES[selectedRoom] || ROOM_TYPES.standardAC;

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      {/* Top Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #0b192c 0%, #1e3e62 100%)",
          color: "#ffffff",
          padding: "36px 0 44px 0",
        }}
      >
        <div className="container">
          <Link
            to="/"
            className="d-inline-flex align-items-center gap-2 text-white text-decoration-none mb-3 small fw-semibold"
          >
            <FaArrowLeft /> <span>Back to Home</span>
          </Link>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h1 className="h2 fw-bold mb-1 font-heading">{currentRoomMeta.title}</h1>
              <p className="text-white-50 mb-0">
                Explore high-resolution room photos, layout details, and amenities.
              </p>
            </div>
            <Link
              to={`/booking?room=${encodeURIComponent(currentRoomMeta.title)}`}
              className="btn-luxury-primary"
            >
              <FaCalendarAlt /> <span>Book This Room</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="container my-4 flex-grow-1">
        {/* Room Switcher Tabs */}
        <div className="d-flex gap-2 flex-wrap mb-4 justify-content-center">
          {validRoomKeys.map((key) => {
            const isSelected = selectedRoom === key;
            return (
              <button
                key={key}
                onClick={() => {
                  setSelectedRoom(key);
                  navigate(`/images/${key}`);
                }}
                className={`btn rounded-pill px-4 py-2 fw-semibold ${isSelected ? "btn-dark shadow-sm" : "btn-outline-secondary bg-white"
                  }`}
                style={{ fontSize: "0.92rem" }}
              >
                {ROOM_TYPES[key].title}
              </button>
            );
          })}
        </div>

        {/* Room Meta Feature Card */}
        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white">
          <div className="row align-items-center g-3">
            <div className="col-12 col-md-6">
              <h3 className="h5 fw-bold text-dark mb-2">Room Specifications</h3>
              <div className="d-flex flex-wrap gap-3 small text-secondary">
                <span className="d-flex align-items-center gap-1">
                  <FaUserFriends className="text-danger" /> {currentRoomMeta.maxGuests}
                </span>
                <span className="d-flex align-items-center gap-1">
                  <FaBed className="text-danger" /> {currentRoomMeta.bed}
                </span>
                <span className="d-flex align-items-center gap-1">
                  <FaSnowflake className="text-info" /> {currentRoomMeta.title.includes("AC") && !currentRoomMeta.title.includes("Non-AC") ? "AC Deluxe" : "Airy & Ventilated"}
                </span>
                <span className="d-flex align-items-center gap-1">
                  <FaWifi className="text-success" /> High-Speed Wi-Fi
                </span>
              </div>
            </div>

            <div className="col-12 col-md-6 text-md-end">
              <span className="text-muted small">Starting from</span>
              <div className="h3 fw-bold text-danger mb-0">₹{getDynamicPrice(selectedRoom).toLocaleString("en-IN")}/night</div>
            </div>

          </div>
        </div>

        {/* Media Content */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading room media...</span>
            </div>
            <p className="text-muted mt-2">Loading gallery photos...</p>
          </div>
        ) : (
          <div>
            <div className="row g-3">
              {media.images.map((imgItem, index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={index}>
                  <div
                    className="gallery-tile position-relative"
                    onClick={() => setCurrentIndex(index)}
                    style={{ height: "240px" }}
                  >
                    <img
                      src={imgItem.url}
                      alt={imgItem.title || `${currentRoomMeta.title} view ${index + 1}`}
                      loading="lazy"
                    />
                    {imgItem.isCover && (
                      <span
                        className="badge position-absolute top-0 start-0 m-2 px-2 py-1 bg-dark text-warning border border-warning"
                        style={{ fontSize: "0.72rem", zIndex: 2 }}
                      >
                        <FaStar className="me-1" /> Cover Photo
                      </span>
                    )}
                    <div className="gallery-tile-overlay">
                      <span className="gallery-tile-caption text-truncate">
                        🔍 {imgItem.title || "Fullscreen View"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Video Tour if available */}
            {media.videos.length > 0 && (
              <div className="mt-5">
                <h4 className="fw-bold text-dark mb-3">Room Video Walkthrough</h4>
                <div className="row g-3">
                  {media.videos.map((vidItem, index) => (
                    <div className="col-12 col-md-6" key={index}>
                      <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow-sm bg-black mb-2">
                        <video controls style={{ width: "100%", height: "100%", objectFit: "contain" }}>
                          <source src={vidItem.url} type="video/mp4" />
                          Your browser does not support video playback.
                        </video>
                      </div>
                      <div className="small fw-semibold text-secondary text-center">
                        {vidItem.title}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lightbox Modal */}
        {currentIndex !== null && (
          <div className="lightbox-backdrop" onClick={() => setCurrentIndex(null)}>
            <div className="lightbox-content-box" onClick={(e) => e.stopPropagation()}>
              <button
                className="lightbox-close-btn"
                onClick={() => setCurrentIndex(null)}
                title="Close (Esc)"
              >
                &times;
              </button>

              <button
                className="lightbox-nav-btn lightbox-nav-left"
                onClick={() =>
                  setCurrentIndex((prev) =>
                    prev > 0 ? prev - 1 : media.images.length - 1
                  )
                }
              >
                <FaChevronLeft />
              </button>

              <img
                src={media.images[currentIndex]?.url}
                alt={media.images[currentIndex]?.title || "Room Gallery"}
                className="lightbox-img"
              />

              <button
                className="lightbox-nav-btn lightbox-nav-right"
                onClick={() =>
                  setCurrentIndex((prev) =>
                    prev < media.images.length - 1 ? prev + 1 : 0
                  )
                }
              >
                <FaChevronRight />
              </button>

              <div className="text-white mt-3 fw-semibold text-center">
                <span>{media.images[currentIndex]?.title || "Room Photo"}</span>
                <span className="text-white-50 ms-2">
                  (Photo {currentIndex + 1} of {media.images.length})
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default RoomImagesPage;
